import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { environment } from 'environments/environment';

import { BaseListReqParams } from 'modules/shared/models/base.models';
import { Customer, CustomerBatchUpload } from 'modules/shared/models/customer.models';
import { PeriodicReviewPayload, PeriodicReviewResponse } from 'modules/shared/models/periodic-review.models';
import { Crp } from 'modules/shared/models/crp.models';
import { AccountListResponse } from 'store/models/admin.models';
import {
  CustomerResponse, CustomerPayload, CreateOrUpdateCustomerPayload,
  GetCustomersBatchUploadsResponse, UploadCustomersBatchFileRequestBody
} from 'store/models/customer.models';
import { DocumentResponse, DocumentPayload } from 'store/models/document.models';
import { CrpResponse, CrpPayload, RecordCrpResponse } from 'store/models/crp.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { RiskReport } from 'modules/shared/models/risk-assessment.models';
import { UserPayload } from 'store/models/customer.models';
import { SearchCustomerIsActiveOptions } from 'modules/shared/models/filter.models';

export interface ICustomersRequest {
  limit?: number;
  name?: string;
  offset?: number;
  search?: string;
  searchAll?: string;
  sort?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  readonly apiBase: string = environment.API_BASE;

  constructor(private httpClient: HttpClient) { }

  getCustomer(id: number): Observable<Customer> {
    return this.httpClient.get<Customer>(`${this.apiBase}/api/customers/${id}/`);
  }

  getCustomers(payload: CustomerPayload): Observable<CustomerResponse> {
    let url = this.apiBase;

    url += '/api/customers/?';
    url += `${payload.limit ? `&limit=${payload.limit}` : ''}`;
    url += `${payload.offset ? `&offset=${payload.offset}` : ''}`;
    url += `${payload.sort ? `&sort=${payload.sort}` : ''}`;
    url += `${payload.name ? `&name=${payload.name}` : ''}`;
    url += `${payload.searchStatus ? `&search_status=${payload.searchStatus}` : ''}`;
    url += `${payload.searchIntegrationStatus ? `&integration_status=${payload.searchIntegrationStatus}` : ''}`;
    url += `${payload.active ? `&is_active_customer=${(payload.active.toUpperCase() === SearchCustomerIsActiveOptions['ACTIVE']) ? 'true' : 'false' }` : ''}`;
    url += `${payload.recordType ? `&record_type=${payload.recordType.toUpperCase()}` : ''}`;
    url += `${payload.searchAll ? `&search_all=${payload.searchAll}` : ''}`;

    return this.httpClient.get<CustomerResponse>(url);
  }

  getCustomersExtended(customersReqBody?: ICustomersRequest): Observable<CustomerResponse> {
    let params: HttpParams = new HttpParams();
    const url: string = this.apiBase + '/api/customers/';

    if (customersReqBody) {
      const reqBodyEntries: Array<[string, any]> = Object.entries(customersReqBody);

      reqBodyEntries.forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }

    return this.httpClient.get<CustomerResponse>(url, { params: params });
  }

  getCustomersBatchUploads(customersReqBody?: BaseListReqParams): Observable<GetCustomersBatchUploadsResponse> {
    let params: HttpParams = new HttpParams();
    const url: string = this.apiBase + '/api/customers/bulk_create/';

    if (customersReqBody) {
      const reqBodyEntries: Array<[string, any]> = Object.entries(customersReqBody);

      reqBodyEntries.forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }

    return this.httpClient.get<GetCustomersBatchUploadsResponse>(url, { params: params });
  }

  getCustomersBatchUploadTemplate(): Observable<Blob> {
    const url = `${this.apiBase}/api/customers/bulk_create_template/`;
    return this.httpClient.get(url, {
      responseType: 'blob'
    });
  }

  getCrp(crpId: number): Observable<Crp> {
    const url = `${this.apiBase}/api/crps/${crpId}/`;
    return this.httpClient.get<Crp>(url);
  }

  getRelatedCrp(recordId: number): Observable<RecordCrpResponse> {
    const url = `${this.apiBase}/api/records/${recordId}/crps/`;
    return this.httpClient.get<RecordCrpResponse>(url);
  }

  getCustomerCrps(payload: CrpPayload): Observable<CrpResponse> {
    let url = `${this.apiBase}/api/customers/${payload.customerId}/crps/?`;
    url += `${payload.limit ? `limit=${payload.limit}` : ''}`;
    url += `${payload.offset ? `&offset=${payload.offset}` : ''}`;
    url += `${payload.search ? `&search=${payload.search}` : ''}`;
    url += `${payload.sort ? `&sort=${payload.sort}` : ''}`;
    if (!payload.primary) {
      url += `&primary=${payload.primary}`;
    }
    if (payload.searchName) {
      url += `&name=${payload.searchName}`;
    }
    return this.httpClient.get<CrpResponse>(url);
  }

  getDocuments(payload: DocumentPayload): Observable<DocumentResponse> {
    let url = `${this.apiBase}/api/records/${payload.id}/documents/?`;
    url += `${payload.limit ? `limit=${payload.limit}` : ''}`;
    url += `${payload.type ? `&doc_type=${payload.type}` : ''}`;
    url += `${payload.offset ? `&offset=${payload.offset}` : ''}`;
    url += `${payload.sort ? `&sort=${payload.sort}` : ''}`;
    return this.httpClient.get<DocumentResponse>(url);
  }

  getCsv(): Observable<any> {
    const url = `${this.apiBase}/api/customers/csv/`;
    return this.httpClient.get(url, {
      responseType: 'json'
    });
  }

  getOnboardingModes() {
    return this.httpClient.get<string[]>(`${this.apiBase}/api/customers/onboarding_modes/`);
  }

  getProfileTypes() {
    return this.httpClient.get<string[]>(`${this.apiBase}/api/customers/profile_types/`);
  }

  getPaymentModes() {
    return this.httpClient.get<string[]>(`${this.apiBase}/api/customers/payment_modes/`);
  }

  getProductComplexities() {
    return this.httpClient.get<string[]>(`${this.apiBase}/api/customers/product_service_complexities/`);
  }

  getCrpsRole() {
    return this.httpClient.get(`${this.apiBase}/api/crps/roles/`);
  }

  getUsers(payload: UserPayload): Observable<AccountListResponse> {
    let url = `${this.apiBase}/api/users/?`;
    url += `${payload.limit ? `limit=${payload.limit}` : ''}`;
    url += `${payload.offset ? `&offset=${payload.offset}` : ''}`;
    if (payload.domain) {
      url += `&domain=${payload.domain}`;
    }
    if (payload.searchName) {
      url += `&name=${payload.searchName}`;
    }
    if (payload.roles) {
      url += `&roles=${payload.roles}`;
    }
    return this.httpClient.get<AccountListResponse>(url);
  }

  getEntityTypes(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/corporate_records/entity_types/';
    return this.httpClient.get<string[]>(url);
  }

  getOwnershipLayers(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/corporate_records/ownership_layers/';
    return this.httpClient.get<string[]>(url);
  }

  getCountries(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/records/countries/';
    return this.httpClient.get<string[]>(url);
  }

  getSsoc(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/individual_records/ssoc/';
    return this.httpClient.get<string[]>(url);
  }

  getIdTypes() {
    let url = this.apiBase;
    url += '/api/individual_records/id_types/';
    return this.httpClient.get<string[]>(url);
  }

  getTitles() {
    let url = this.apiBase;
    url += '/api/individual_records/titles/';
    return this.httpClient.get<string[]>(url);
  }

  getSourceOfFunds() {
    let url = this.apiBase;
    url += '/api/records/source_of_funds/';
    return this.httpClient.get<string[]>(url);
  }

  getSsic(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/records/ssic/';
    return this.httpClient.get<string[]>(url);
  }

  getProductServiceComplexities(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/customers/product_service_complexities/';
    return this.httpClient.get<string[]>(url);
  }

  getGenders(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/individual_records/gender/';
    return this.httpClient.get<string[]>(url);
  }

  getStatuses(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/crps/status/';
    return this.httpClient.get<string[]>(url);
  }
  getSourceOfWealth(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/customers/source_of_wealth/';
    return this.httpClient.get<string[]>(url);
  }

  getAnnualIncomeLevelUsd(): Observable<string[]> {
    let url = this.apiBase;
    url += '/api/customers/annual_income_level/';
    return this.httpClient.get<string[]>(url);
  }

  getAppConfig(): Observable<any> {
    let url = this.apiBase;
    url += '/api/appconfig/AVAILABLE_FORM_FIELDS/';
    return this.httpClient.get<any>(url);
  }

  createOrUpdateCustomer(createOrUpdateCustomerPayload: CreateOrUpdateCustomerPayload): Observable<Customer> {
    let url = this.apiBase;

    if (createOrUpdateCustomerPayload.customerData.individualRecords &&
      createOrUpdateCustomerPayload.customerData.individualRecords.length) {
      createOrUpdateCustomerPayload.customerData.individualRecords.map((individualRecord: IndividualRecord) => {
        return this.deleteNullContent(individualRecord);
      });
    }

    if (createOrUpdateCustomerPayload.customerData.corporateRecords &&
      createOrUpdateCustomerPayload.customerData.corporateRecords.length) {
      createOrUpdateCustomerPayload.customerData.corporateRecords.map((individualRecord: CorporateRecord) => {
        return this.deleteNullContent(individualRecord);
      });
    }

    if (createOrUpdateCustomerPayload.mode === 'CREATE') {
      url += '/api/customers/';
      return this.httpClient.post<Customer>(url, this.deleteNullContent(createOrUpdateCustomerPayload.customerData));
    } else {
      url += `/api/customers/${createOrUpdateCustomerPayload.customerId}/`;
      return this.httpClient.patch<Customer>(url, this.deleteNullContent(createOrUpdateCustomerPayload.customerData));
    }
  }

  patchCustomer(payload: Customer): Observable<Customer> {
    const url = `${this.apiBase}/api/customers/${payload.id}/`;
    return this.httpClient.patch<Customer>(url, payload);
  }

  deleteNullContent(object: any) {
    for (const i in object) {
      if (object[i] === null &&
        ['idExpiryDate', 'idIssueDate', 'dateOfBirth', 'incorporationDate', 'incorporationExpiryDate'].indexOf(i) < 0
      ) {
        object[i] = '';
      }
    }
    return object;
  }

  createRiskReports(customerId: number): Observable<RiskReport> {
    return this.httpClient.post<RiskReport>(`${this.apiBase}/api/customers/${customerId}/risk_reports/`, {});
  }

  downloadReport(customerId: number): Observable<Blob> {
    const url = `${this.apiBase}/api/customers/${customerId}/pdf_report/`;
    return this.httpClient.get(url, {
      responseType: 'blob'
    });
  }

  downloadAcceptanceForm(customerId: number): Observable<Blob> {
    const url = `${this.apiBase}/api/customers/${customerId}/acceptance_form/`;
    return this.httpClient.get(url, {
      responseType: 'blob'
    });
  }

  /**
   * Returns a list of periodic review
   * @method loadPeriodicReview
   */
  loadPeriodicReview(payload?: PeriodicReviewPayload): Observable<PeriodicReviewResponse> {
    let params: HttpParams = new HttpParams();

    let url = this.apiBase;
    url += '/api/customers/periodic_reviews/?';

    if (payload) {
      Object.entries(payload).forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }
    return this.httpClient.get<PeriodicReviewResponse>(`${url}`, {params: params});
  }

  /**
   * Delete a customer
   * @method deleteCustomer
   */
  deleteCustomer(customerId: number): Observable<any> {
    return this.httpClient.delete(`${this.apiBase}/api/customers/${customerId}/`, { observe: 'response' });
  }

  uploadCustomersBatchFile(params: UploadCustomersBatchFileRequestBody): Observable<CustomerBatchUpload> {
    const url: string = this.apiBase + '/api/customers/bulk_create/';
    const form = new FormData();
    form.append('file', params.file);

    params.users.forEach((user: string) => {
      form.append('users', user);
    });

    params.domains.forEach((domain: string) => {
      form.append('domains', domain);
    });

    return this.httpClient.post<CustomerBatchUpload>(url, form);
  }
}
